import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Search, X, Clock } from "lucide-react";
import {
  useFractionTransform,
  holdsRevertedText,
} from "@/hooks/use-fraction-transform";
import type { QueryChange } from "@/hooks/use-product-search";
import { useProductEvents } from "@/hooks/use-product-events";

interface SearchSuggestion {
  id: string;
  text: string;
  type: "product" | "recent";
}

interface ProductSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  showSuggestions?: boolean;
  recentSearches?: string[];
  onRecentSearchSelect?: (search: string) => void;
  onClearRecentSearches?: () => void;
  correctedQuery?: string;
  queryChanges?: QueryChange[];
  onRevert?: (originalQuery: string) => void;
}

const MIN_SEARCH_LENGTH = 2;

// Mock popular searches and categories for suggestions
const POPULAR_SEARCHES = [
  "mainline garbage disposal",
  "2 gal expansion tank",
  "3/4 in x 20 ft copper pipe",
  "1-1/2 inch pvc bushing",
  "4 in abs 90 elbow",
];

function CorrectedQueryDisplay({ text, changes, highlightFrom }: { text: string; changes: QueryChange[]; highlightFrom?: boolean }) {
  const highlightedWords = new Set<string>();
  for (const change of changes) {
    const source = highlightFrom ? change.from : change.to;
    for (const word of source.toLowerCase().split(/\s+/)) {
      highlightedWords.add(word);
    }
  }

  const parts = text.split(/(\s+)/);
  return (
    <>
      {parts.map((part, i) => {
        if (/^\s+$/.test(part)) return <span key={i}>{part}</span>;
        const isCorrected = highlightedWords.has(part.toLowerCase());
        return isCorrected ? (
          <span key={i} className="underline decoration-dotted decoration-red-500 underline-offset-4">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </>
  );
}

export const ProductSearchInput: React.FC<ProductSearchInputProps> = ({
  value,
  onChange,
  placeholder = i18n.t("components.products.productSearchInput.searchProducts"),
  disabled = false,
  showSuggestions = true,
  recentSearches = [],
  onRecentSearchSelect,
  onClearRecentSearches,
  correctedQuery,
  queryChanges,
  onRevert,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  // Text the box held before the server correction rewrote it.
  const [correctionOriginal, setCorrectionOriginal] = useState<string | null>(null);
  // The text a correction revert put back, latching corrections off while the
  // box still holds it. Null once the user has moved on to a different query.
  const [revertedCorrection, setRevertedCorrection] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // The correction already written into the box, so it is applied only once.
  const lastAppliedCorrectionRef = useRef<string | null>(null);
  // Word-level changes behind the current correction, used to underline them.
  const [activeCorrectionChanges, setActiveCorrectionChanges] = useState<QueryChange[] | null>(null);
  // Links a correction event to the revert event that may follow it.
  const correctionTransformationIdRef = useRef<string | null>(null);
  const { capture } = useProductEvents({ component: "ProductSearchInput" });

  // Generate suggestions based on input
  const generateSuggestions = useCallback(
    (query: string): SearchSuggestion[] => {
      const suggestions: SearchSuggestion[] = [];
      const lowerQuery = query.toLowerCase();

      if (query.length === 0) {
        // Show recent searches when empty
        if (recentSearches.length > 0) {
          suggestions.push(
            ...recentSearches.slice(0, 5).map((search) => ({
              id: `recent-${search}`,
              text: search,
              type: "recent" as const,
            })),
          );
        }

        // Show popular searches
        suggestions.push(
          ...POPULAR_SEARCHES.slice(0, 5).map((search) => ({
            id: `popular-${search}`,
            text: search,
            type: "product" as const,
          })),
        );
      } else if (query.length >= MIN_SEARCH_LENGTH) {
        // Show filtered popular searches
        const matchingSearches = POPULAR_SEARCHES.filter((search) =>
          search.toLowerCase().includes(lowerQuery),
        );
        suggestions.push(
          ...matchingSearches.slice(0, 5).map((search) => ({
            id: `suggestion-${search}`,
            text: search,
            type: "product" as const,
          })),
        );
      }

      return suggestions;
    },
    [recentSearches],
  );

  // Rewrites fraction shorthand in the box once typing settles.
  const fraction = useFractionTransform({
    // Puts the fraction in the box and refreshes the dropdown under it.
    onTransform: (transformed) => {
      setInputValue(transformed);
      const transformedSuggestions = generateSuggestions(transformed);
      setSuggestions(transformedSuggestions);
      setShowDropdown(showSuggestions && transformedSuggestions.length > 0);
    },
    // Puts the typed text back without searching it. This box only searches on
    // Enter or the button, so taking a rewrite back is an edit like any other:
    // the text returns, the cursor stays put, and the user decides when to run
    // it. (The server-correction banner below does search on revert, because a
    // correction only ever appears after a search has already returned.)
    onRevert: (original) => {
      setInputValue(original);
      const originalSuggestions = generateSuggestions(original);
      setSuggestions(originalSuggestions);
      setShowDropdown(showSuggestions && originalSuggestions.length > 0);
      inputRef.current?.focus();
    },
    capture,
  });

  // Writes the server's typo/alias correction into the box. This runs even
  // with a fraction rewrite showing — the box has to read as what was actually
  // searched — and the banner below renders the correction instead of the
  // fraction offer, so the two never stack.
  useEffect(() => {
    // Nothing to apply, or the user is still on the text they kept instead.
    if (!correctedQuery || holdsRevertedText(inputValue, revertedCorrection)) return;

    const correctedLower = correctedQuery.toLowerCase();
    const inputLower = inputValue.toLowerCase().trim();

    // The box already reads as the correction does.
    if (correctedLower === inputLower) return;
    // This correction has been applied once already.
    if (lastAppliedCorrectionRef.current === correctedQuery) return;

    lastAppliedCorrectionRef.current = correctedQuery;
    const id = `transform_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    correctionTransformationIdRef.current = id;
    setCorrectionOriginal(inputValue);
    setInputValue(correctedQuery);
    setActiveCorrectionChanges(queryChanges ?? null);

    const changes = queryChanges ?? [];
    const hasTypo = changes.some((c) => c.type === "typo");
    capture({
      event: "query_transformed",
      context: {
        // Pairs this event with the revert that may follow.
        transformationId: id,
        // What the user typed.
        rawQuery: inputValue,
        // What the server matched on.
        transformedQuery: correctedQuery,
        // Separates a spelling fix from an abbreviation being expanded.
        transformationType: hasTypo ? "typo_correction" : "alias_expansion",
        // The individual words that changed.
        queryChanges: changes,
      },
    });
  }, [correctedQuery]); // eslint-disable-line react-hooks/exhaustive-deps


  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // An emptied box starts over, corrections included. holdsRevertedText
    // can't see this on its own — "" is a prefix of every reverted text — so
    // the latch is dropped here.
    if (newValue.length === 0) {
      setRevertedCorrection(null);
    }

    // Editing dismisses the correction banner and lets the next response
    // correct the box again.
    setCorrectionOriginal(null);
    setActiveCorrectionChanges(null);
    lastAppliedCorrectionRef.current = null;

    setInputValue(newValue);

    // Update suggestions immediately for UI responsiveness
    const newSuggestions = generateSuggestions(newValue);
    setSuggestions(newSuggestions);

    // Show dropdown if we have suggestions
    setShowDropdown(showSuggestions && newSuggestions.length > 0);

    // Rewrite fraction shorthand once the typing settles.
    fraction.handleInputChange(newValue);

    // Note: onChange is only called when user explicitly triggers search (Enter key or suggestion selection)
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: SearchSuggestion) => {
    setInputValue(suggestion.text);
    onChange(suggestion.text);
    setShowDropdown(false);
    // A picked suggestion replaces the text, so neither banner still applies
    // and the next response may correct the new text freely.
    fraction.reset();
    setCorrectionOriginal(null);
    setActiveCorrectionChanges(null);
    lastAppliedCorrectionRef.current = null;

    if (suggestion.type === "recent" && onRecentSearchSelect) {
      onRecentSearchSelect(suggestion.text);
    }
  };

  // Puts the text the user typed back and searches it uncorrected.
  const handleRevertCorrection = () => {
    if (correctionOriginal === null) return;
    capture({
      event: "query_transform_reverted",
      context: {
        // Matches the id sent with the correction being undone.
        transformationId: correctionTransformationIdRef.current ?? "unknown",
        // The correction the box was showing.
        transformedQuery: inputValue,
        // The text going back in.
        revertedQuery: correctionOriginal,
      },
    });
    setInputValue(correctionOriginal);
    if (onRevert) {
      onRevert(correctionOriginal);
    } else {
      onChange(correctionOriginal);
    }
    setCorrectionOriginal(null);
    setRevertedCorrection(correctionOriginal);
    setActiveCorrectionChanges(null);
    lastAppliedCorrectionRef.current = null;
    correctionTransformationIdRef.current = null;
    const originalSuggestions = generateSuggestions(correctionOriginal);
    setSuggestions(originalSuggestions);
    setShowDropdown(showSuggestions && originalSuggestions.length > 0);
    inputRef.current?.focus();
  };

  // Handle clear
  const handleClear = () => {
    setInputValue("");
    onChange("");
    setSuggestions(generateSuggestions(""));
    setShowDropdown(false);
    // An emptied box starts over: no banners, both transforms live again.
    fraction.reset();
    setCorrectionOriginal(null);
    setRevertedCorrection(null);
    setActiveCorrectionChanges(null);
    lastAppliedCorrectionRef.current = null;
    inputRef.current?.focus();
  };

  // Handle focus
  const handleFocus = () => {
    if (showSuggestions) {
      const newSuggestions = generateSuggestions(inputValue);
      setSuggestions(newSuggestions);
      setShowDropdown(newSuggestions.length > 0);
    }
  };

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const container = inputRef.current?.closest(".relative");
      const dropdown = dropdownRef.current;

      // Don't close if clicking inside the input container or dropdown
      if (container?.contains(target) || dropdown?.contains(target)) {
        return;
      }

      setShowDropdown(false);
    };

    if (showDropdown) {
      // Use a small delay to allow CommandItem onSelect to fire first
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Sync external value changes only if input is not focused
  useEffect(() => {
    // Only update if input is not currently focused to prevent losing focus
    if (document.activeElement !== inputRef.current) {
      setInputValue(value);
    }
  }, [value]);

  // Handle search button click
  const handleSearch = () => {
    // Pressing search finishes the word being typed, so a fraction rewrite the
    // debounce was holding back applies now and the search runs on it.
    const committed = fraction.commit(inputValue);

    onChange(committed);
    setShowDropdown(false);
    inputRef.current?.blur();
  };

  // Keyboard handling
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setShowDropdown(false);
      inputRef.current?.blur();
    } else if (e.key === "Enter") {
      // Prevent form submission when Enter is pressed
      e.preventDefault();
      // Trigger search when user presses Enter
      handleSearch();
    }
  };

  return (
    <div className="relative w-full">
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
            <Search className="h-4 w-4" />
          </div>
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className="pl-10 pr-10 h-12 text-base"
          />
          {inputValue && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-transparent"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Button
          type="button"
          onClick={handleSearch}
          disabled={disabled}
          className="h-12 px-4 md:px-6 shrink-0"
          aria-label={t("components.products.productSearchInput.search")}
        >
          <Search className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">{t("components.products.productSearchInput.search")}</span>
        </Button>
      </div>

      {/* Correction banner, shown instead of the fraction one when both apply. */}
      {correctionOriginal !== null && activeCorrectionChanges && activeCorrectionChanges.length > 0 && (
        <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
          <span className="text-muted-foreground">{t("components.products.productSearchInput.keep")} </span>
          <span
            role="button"
            tabIndex={0}
            onClick={handleRevertCorrection}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleRevertCorrection(); }}
            className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
          >
            <CorrectedQueryDisplay text={correctionOriginal} changes={activeCorrectionChanges} highlightFrom />
          </span>
          <span className="text-muted-foreground"> {t("components.products.productSearchInput.asYourSearchTerm")}</span>
        </div>
      )}

      {/* Fraction banner, offering the typed text back. Gated on the same
          value as the correction banner above, so exactly one of the two can
          claim the slot whatever the server sends. */}
      {!activeCorrectionChanges?.length && fraction.original !== null && (
        <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
          <span className="text-muted-foreground">{t("components.products.productSearchInput.keep")} </span>
          <button
            type="button"
            onClick={fraction.revert}
            className="text-blue-600 hover:text-blue-800 font-bold underline"
          >
            {fraction.original}
          </button>
          <span className="text-muted-foreground"> {t("components.products.productSearchInput.asYourSearchTerm")}</span>
        </div>
      )}

      {/* Suggestions Dropdown - Positioned Absolutely */}
      {showSuggestions && showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 z-50 mt-1 bg-popover border rounded-md shadow-lg"
        >
          <Command>
            <CommandList>
              {recentSearches.length > 0 &&
                suggestions.some((s) => s.type === "recent") && (
                  <CommandGroup>
                    <div className="flex items-center justify-between px-2 py-1.5 text-xs font-medium text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {t("components.products.productSearchInput.recentSearches")}
                      </span>
                      {onClearRecentSearches && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={onClearRecentSearches}
                          className="h-6 px-2 text-xs"
                        >
                          {t("components.products.productSearchInput.clear")}
                        </Button>
                      )}
                    </div>
                    {suggestions
                      .filter((s) => s.type === "recent")
                      .map((suggestion) => (
                        <CommandItem
                          key={suggestion.id}
                          value={suggestion.id}
                          onSelect={() => handleSuggestionSelect(suggestion)}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span>{suggestion.text}</span>
                        </CommandItem>
                      ))}
                  </CommandGroup>
                )}

              {suggestions.some((s) => s.type === "product") && (
                <CommandGroup
                  heading={inputValue ? t("components.products.productSearchInput.suggestions") : t("components.products.productSearchInput.popularSearches")}
                >
                  {suggestions
                    .filter((s) => s.type === "product")
                    .map((suggestion) => (
                      <CommandItem
                        key={suggestion.id}
                        value={suggestion.id}
                        onSelect={() => handleSuggestionSelect(suggestion)}
                        className="cursor-pointer"
                      >
                        <Search className="mr-2 h-3 w-3 text-muted-foreground" />
                        <span>{suggestion.text}</span>
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
};
